import type { QuickFillSnapshotShapeOption } from '../../../shared/api/grpc/familyClient';
import {
  getEquivalentRevitParameterNames,
  normalizeRevitParameterName,
  sanitizeRevitParameterNames,
} from '../../../shared/lib/revitParameterNames';
import {
  applyAutoFillToQuickFillItem,
  autoFillFromQuickFillItem,
  isModelTextAutoFillResult,
} from '../../../shared/lib/utils/autoFillService';
import {
  createDefaultQuickFillProperties,
  useQuickFillStore,
} from '../../../shared/stores/useQuickFillStore';
import { collectMappedQuickFillColumns } from '../hooks/useQuickFillConfigSync';
import type {
  ConnectorBindingConfiguration,
  MappingConfiguration,
  MenuConfiguration,
  QuickFillItem,
  QuickFillProperty,
} from '../../../shared/types';
import {
  saveCellsToRevit,
  type BulkUpdateResult,
  type PropertyMapping,
  type QuickFillSyncProgress,
} from '../../../shared/services/quickFillRevitService';
import {
  fetchQuickFillSnapshot,
  type QuickFillDataSnapshot,
} from '../hooks/useQuickFillData';
import { buildQuickFillDataAsync } from './quickFillDataTransforms';
import {
  buildEditedQuickFillCellUpdates,
  resolveQuickFillPropertyBindingScope,
} from './quickFillPageModel';

export interface HeadlessQuickFillRepairStage {
  title: string;
  message: string;
  detail?: string;
}

export interface HeadlessQuickFillAutoFillRepairInput {
  projectKey: string | null;
  modelVersion: number | null;
  menuConfig: MenuConfiguration;
  connectorConfig: ConnectorBindingConfiguration;
  mappingConfiguration?: MappingConfiguration;
  // 'classification' writes only SKEY/OBJECT-TYPE (the values bolt/additional
  // family searches depend on) and is safe before parameter creation finishes;
  // 'full' is the page-equivalent autofill and must run after all setup steps.
  scope?: 'classification' | 'full';
  repairProjectBindings?: Array<{
    parameterName: string;
    bindingKind: 'type' | 'instance';
  }>;
  snapshotShape?: QuickFillSnapshotShapeOption;
  onStage?: (stage: HeadlessQuickFillRepairStage) => void;
  onSaveProgress?: (progress: QuickFillSyncProgress) => void;
}

export interface HeadlessQuickFillAutoFillRepairResult {
  success: boolean;
  attempted: boolean;
  filledCount: number;
  savedCount: number;
  failureCount: number;
  totalRows: number;
  updateCount: number;
  error?: string;
}

interface HeadlessQuickFillAutoFillRepairDependencies {
  fetchSnapshot?: (
    parameterNames?: string[],
    options?: {
      snapshotShape?: QuickFillSnapshotShapeOption;
      cacheScopeKey?: string | null;
    }
  ) => Promise<QuickFillDataSnapshot>;
  saveCells?: typeof saveCellsToRevit;
}

const AutoFillRepairPropertyIds = new Set([
  'skey',
  'object-type',
  'end1',
  'end2',
  'end3',
  'end4',
]);

const AutoFillClassificationPropertyIds = new Set(['skey', 'object-type']);

function buildQuickFillRepairCacheScope(projectKey: string | null, modelVersion: number | null): string | null {
  if (!projectKey) {
    return null;
  }

  return `${projectKey}::v${modelVersion ?? 0}`;
}

function cloneQuickFillRows(rows: QuickFillItem[]): QuickFillItem[] {
  return JSON.parse(JSON.stringify(rows)) as QuickFillItem[];
}

function valuesAreDifferent(left: unknown, right: unknown): boolean {
  return String(left ?? '') !== String(right ?? '');
}

function canPersistAutoFilledValue(row: QuickFillItem, label: string): boolean {
  const hasParameterFlags = (row as QuickFillItem & {
    __hasParamFlags?: Record<string, boolean>;
  }).__hasParamFlags;
  const readOnlyFlags = (row as QuickFillItem & {
    __readonlyFlags?: Record<string, boolean>;
  }).__readonlyFlags;

  return hasParameterFlags?.[label] !== false && readOnlyFlags?.[label] !== true;
}

function buildLocalAutoFilledRows(
  rows: QuickFillItem[],
  labels: string[]
): {
  rows: QuickFillItem[];
  editedCells: Record<string, boolean>;
  filledCount: number;
} {
  const editedCells: Record<string, boolean> = {};
  let filledCount = 0;

  const updatedRows = rows.map((row, rowIndex) => {
    const result = autoFillFromQuickFillItem(row);
    if (isModelTextAutoFillResult(result)) {
      // Writes for Model Text rows always fail (not a loadable family), so
      // filling them here only produces unsavable updates.
      return row;
    }

    const updatedRow = applyAutoFillToQuickFillItem(row, result, labels, 'fillEmpty');
    let rowChanged = false;

    labels.forEach((label) => {
      if (
        canPersistAutoFilledValue(row, label) &&
        valuesAreDifferent(row[label], updatedRow[label])
      ) {
        editedCells[`${rowIndex}-${label}`] = true;
        rowChanged = true;
      }
    });

    if (rowChanged) {
      filledCount += 1;
    }

    return updatedRow;
  });

  return {
    rows: updatedRows,
    editedCells,
    filledCount,
  };
}

function buildConfigMappedQuickFillProperties(input: {
  menuConfig: MenuConfiguration;
  connectorConfig: ConnectorBindingConfiguration;
  checkedIds?: Set<string>;
}): QuickFillProperty[] {
  const checkedIds = input.checkedIds ?? AutoFillRepairPropertyIds;
  const configMapping: Record<string, string> = {
    skey: input.menuConfig.sKeyValue || '',
    'object-type': input.menuConfig.objectTypeValue || '',
    end1: input.connectorConfig.endpoint1Value?.value || '',
    end2: input.connectorConfig.endpoint2Value?.value || '',
    end3: input.connectorConfig.endpoint3Value?.value || '',
    end4: input.connectorConfig.endpoint4Value?.value || '',
  };

  return createDefaultQuickFillProperties().map((property) => {
    const mappedParameter = configMapping[property.id]?.trim();
    return {
      ...property,
      ...(mappedParameter ? { revitParameter: mappedParameter } : {}),
      checked: checkedIds.has(property.id),
    };
  });
}

/**
 * Page-equivalent property set: the config-mapped base labels (the same
 * source the page syncs its properties from) plus the Quick Fill store's
 * extra properties and the iso mapping table's mapped columns — the same
 * extras/columns the page's request builder appends, so the repair autofill
 * writes the same cells the Quick Fill page would.
 */
function buildPageEquivalentQuickFillProperties(input: {
  menuConfig: MenuConfiguration;
  connectorConfig: ConnectorBindingConfiguration;
  mappingConfiguration?: MappingConfiguration;
}): QuickFillProperty[] {
  const baseProperties = buildConfigMappedQuickFillProperties(input);
  const extraProperties = useQuickFillStore.getState().extraProperties ?? [];
  const mappedColumns = collectMappedQuickFillColumns(
    input.mappingConfiguration,
    baseProperties,
    extraProperties
  );

  return [...baseProperties, ...extraProperties, ...mappedColumns];
}

function resolveRepairQuickFillProperties(input: HeadlessQuickFillAutoFillRepairInput): QuickFillProperty[] {
  const properties = input.scope === 'classification'
    ? buildConfigMappedQuickFillProperties({
      ...input,
      checkedIds: AutoFillClassificationPropertyIds,
    })
    : buildPageEquivalentQuickFillProperties(input);
  if (input.repairProjectBindings === undefined) {
    return properties;
  }

  const bindingsByName = new Map<string, 'type' | 'instance'>();
  input.repairProjectBindings.forEach(({ parameterName, bindingKind }) => {
    getEquivalentRevitParameterNames(parameterName).forEach((name) => {
      bindingsByName.set(name.trim().toLowerCase(), bindingKind);
    });
  });
  return properties.flatMap((property) => {
    const bindingKind = getEquivalentRevitParameterNames(property.revitParameter ?? '')
      .map((name) => bindingsByName.get(name.trim().toLowerCase()))
      .find((value): value is 'type' | 'instance' => value !== undefined);
    if (!bindingKind || !property.checked) {
      return [];
    }

    return [{ ...property, isInstance: bindingKind === 'instance' }];
  });
}

function getRepairLabels(properties: QuickFillProperty[]): string[] {
  return properties
    .filter((property) => property.checked && property.revitParameter && property.label !== 'Name')
    .map((property) => property.label);
}

function getRepairParameterNames(properties: QuickFillProperty[]): string[] {
  return sanitizeRevitParameterNames(
    properties
      .filter((property) => property.checked)
      .map((property) => property.revitParameter)
      .filter((value): value is string => typeof value === 'string' && value.trim() !== '')
  ).map(normalizeRevitParameterName);
}

function getRepairPropertyMappings(properties: QuickFillProperty[]): PropertyMapping[] {
  return properties
    .filter(
      (property): property is QuickFillProperty & { revitParameter: string } =>
        property.checked &&
        property.label !== 'Name' &&
        typeof property.revitParameter === 'string' &&
        property.revitParameter.trim() !== ''
    )
    .map((property) => {
      const bindingScope = resolveQuickFillPropertyBindingScope(property);
      return {
        label: property.label,
        revitParameter: property.revitParameter,
        ...(bindingScope ? { bindingScope } : {}),
      };
    });
}

function getBulkUpdateError(result: BulkUpdateResult): string | undefined {
  return result.results.find((entry) => !entry.success)?.error;
}

export async function runHeadlessQuickFillAutoFillRepair(
  input: HeadlessQuickFillAutoFillRepairInput,
  dependencies: HeadlessQuickFillAutoFillRepairDependencies = {}
): Promise<HeadlessQuickFillAutoFillRepairResult> {
  const snapshotShape = input.snapshotShape ?? 'familyType';
  const properties = resolveRepairQuickFillProperties(input);
  const labels = getRepairLabels(properties);
  const parameterNames = getRepairParameterNames(properties);
  const propertyMappings = getRepairPropertyMappings(properties);
  const fetchSnapshot = dependencies.fetchSnapshot ?? fetchQuickFillSnapshot;
  const saveCells = dependencies.saveCells ?? saveCellsToRevit;

  if (input.repairProjectBindings !== undefined && parameterNames.length === 0) {
    return {
      success: true,
      attempted: false,
      filledCount: 0,
      savedCount: 0,
      failureCount: 0,
      totalRows: 0,
      updateCount: 0,
    };
  }

  try {
    input.onStage?.({
      title: 'Classifying project components',
      message: 'Quick Fill auto-fill is loading project family data',
      detail: `${parameterNames.length} mapped classification parameter${parameterNames.length === 1 ? '' : 's'} will be inspected.`,
    });
    const snapshot = await fetchSnapshot(parameterNames, {
      snapshotShape,
      cacheScopeKey: buildQuickFillRepairCacheScope(input.projectKey, input.modelVersion),
    });

    input.onStage?.({
      title: 'Classifying project components',
      message: 'Quick Fill auto-fill is preparing rows',
      detail: `${snapshot.families.length} family bucket${snapshot.families.length === 1 ? '' : 's'} loaded for repair.`,
    });
    const buildResult = await buildQuickFillDataAsync(
      snapshot.families,
      snapshot.pipeElements,
      snapshot.availableParameters,
      properties,
      [],
      [],
      {},
      false,
      'familyType',
      {},
      () => true,
      () => undefined,
      snapshotShape
    );
    const rows = buildResult.data ?? [];
    const baselineRows = cloneQuickFillRows(rows);

    input.onStage?.({
      title: 'Classifying project components',
      message: 'Quick Fill auto-fill is filling empty setup values',
      detail: `${rows.length} row${rows.length === 1 ? '' : 's'} will be checked without overwriting existing values.`,
    });
    const autoFillResult = buildLocalAutoFilledRows(baselineRows, labels);
    const updates = buildEditedQuickFillCellUpdates(
      autoFillResult.editedCells,
      autoFillResult.rows,
      propertyMappings
    );

    if (updates.length === 0) {
      return {
        success: true,
        attempted: true,
        filledCount: autoFillResult.filledCount,
        savedCount: 0,
        failureCount: 0,
        totalRows: rows.length,
        updateCount: 0,
      };
    }

    input.onStage?.({
      title: 'Saving Quick Fill classifications',
      message: 'Revit is saving generated Quick Fill setup values',
      detail: `${updates.length} generated value${updates.length === 1 ? '' : 's'} will be written to the project.`,
    });
    const saveResult = await saveCells(autoFillResult.rows, updates, {
      onProgress: input.onSaveProgress,
    });

    return {
      success: saveResult.failureCount === 0,
      attempted: true,
      filledCount: autoFillResult.filledCount,
      savedCount: saveResult.successCount,
      failureCount: saveResult.failureCount,
      totalRows: rows.length,
      updateCount: updates.length,
      ...(saveResult.failureCount > 0
        ? { error: getBulkUpdateError(saveResult) ?? 'Some generated Quick Fill values could not be saved.' }
        : {}),
    };
  } catch (error) {
    return {
      success: false,
      attempted: true,
      filledCount: 0,
      savedCount: 0,
      failureCount: 1,
      totalRows: 0,
      updateCount: 0,
      error: error instanceof Error ? error.message : 'Quick Fill auto-fill repair failed.',
    };
  }
}
